import React, { useEffect, useState } from "react";
import { Layers, Info, Clock, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export const BATCH_DEFINITION = "Metode batch: tugas dikirim ke antrean khusus penyedia AI (OpenAI, Anthropic, Gemini) dan diproses saat kapasitas tersedia — bukan seketika. Imbalannya tarif 50% lebih murah; hasil paling lambat 24 jam (biasanya lebih cepat), tersimpan di Ruang Kerja, dan Anda diberi tahu lewat notifikasi.";

// Asks the backend whether the provider Batch API exists for a model / an assistant's model.
export function useBatchSupport({ model, personaId } = {}) {
  const [s, setS] = useState({ supported: false, discount: 0.5, window_hours: 24 });
  useEffect(() => {
    if (!model && !personaId) return;
    api.post("/batch/support", { model: model || null, persona_id: personaId || null }).then((r) => setS(r.data)).catch(() => {});
  }, [model, personaId]);
  return s;
}

export function BatchInfo({ children }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed" data-testid="batch-tooltip">{BATCH_DEFINITION}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Pill toggle: "Metode batch · hemat 50%" with an info tooltip explaining what batch means.
export function BatchToggle({ on, onChange, disabled, size = "sm", testId = "batch-toggle" }) {
  const pad = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} data-testid={testId}
        className={`inline-flex items-center gap-1.5 rounded-full border font-bold transition disabled:opacity-60 ${pad} ${on ? "border-emerald-500 bg-emerald-500 text-white" : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}>
        <Layers size={13} /> Metode batch · hemat 50%{on && " ✓"}
      </button>
      <BatchInfo><button type="button" aria-label="Apa itu metode batch?" data-testid={`${testId}-info`} className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"><Info size={14} /></button></BatchInfo>
    </span>
  );
}

// Workspace task header: waiting state + cancel, or the savings once done.
export function BatchTaskPanel({ task, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (task?.method !== "batch") return null;
  const b = task.batch || {};
  const cancel = async () => {
    setBusy(true);
    try { await api.post(`/tasks/${task.id}/batch-cancel`); toast.success("Batch dibatalkan — tidak ada kredit yang dipotong"); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); } finally { setBusy(false); }
  };
  if (task.status === "running" && b.state === "pending") {
    const since = b.submitted_at ? new Date(b.submitted_at).toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "";
    return (
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="batch-pending-panel">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white"><Layers size={16} /></span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-800">Metode batch · menunggu hasil <BatchInfo><Info size={13} className="cursor-help text-emerald-600" /></BatchInfo></p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-emerald-700"><Clock size={12} /> Dikirim {since} · hasil paling lambat 24 jam · tarif 50% lebih hemat · Anda akan diberi tahu lewat notifikasi</p>
        </div>
        <button onClick={cancel} disabled={busy} data-testid="batch-cancel-btn" className="flex items-center gap-1.5 rounded-xl border border-[#EF4444]/40 bg-white px-3 py-2 text-xs font-semibold text-[#EF4444] hover:bg-[#EF4444]/10 disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Batalkan batch
        </button>
      </div>
    );
  }
  if (task.status === "completed") {
    return <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="batch-saved-note"><Layers size={13} /> Dikerjakan dengan metode batch — hemat {Number(b.saved_credits || task.credits_used || 0).toFixed(1)} kredit (50%)</p>;
  }
  return null;
}
